import { useTranslate } from '@refinedev/core';
import { DateField, useTable } from '@refinedev/antd';
import { Flex, Table, Tag, theme } from 'antd';
import type {
  EntitlementProjectionFailure,
  RevenueCatBillingPeriodOut,
} from '@/api/generated/schemas';
import { EmbeddedTableSearch } from '@/components/common/EmbeddedTableSearch';
import { ShowLink } from '@/components/common/ShowLink';
import { ProjectionFailureTag } from '@/components/monetization/ProjectionFailureTag';
import { getSortOrder } from '@/utils/sorters';

interface CustomerBillingPeriodsTableProps {
  accountId: number;
}

export const CustomerBillingPeriodsTable = ({
  accountId,
}: CustomerBillingPeriodsTableProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { tableProps, sorters, setFilters } =
    useTable<RevenueCatBillingPeriodOut>({
      resource: 'monetization.revenuecat_billing_periods',
      filters: {
        permanent: [{ field: 'account_id', operator: 'eq', value: accountId }],
      },
      sorters: { initial: [{ field: 'valid_from', order: 'desc' }] },
      pagination: { pageSize: 10 },
      syncWithLocation: false,
    });
  const f = (key: string) =>
    t(`monetization.revenuecat_billing_periods.fields.${key}`);

  return (
    <Flex vertical gap={token.marginSM}>
      <EmbeddedTableSearch
        setFilters={setFilters}
        placeholder={t(
          'monetization.revenuecat_billing_periods.search.placeholder'
        )}
      />
      <Table<RevenueCatBillingPeriodOut>
        {...tableProps}
        rowKey="id"
        size="small"
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('messages.no_data') }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowLink
              resource="monetization.revenuecat_billing_periods"
              id={value}
            />
          )}
        />
        <Table.Column
          dataIndex="status"
          title={f('status')}
          sorter
          sortOrder={getSortOrder(sorters, 'status')}
          render={(value: string) => (
            <Tag color={value === 'active' ? 'success' : 'default'}>
              {t(`monetization.revenuecat_billing_periods.status.${value}`, {
                defaultValue: value,
              })}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="product_id"
          title={f('product_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_id')}
          render={(value: number, record: RevenueCatBillingPeriodOut) => (
            <ShowLink resource="monetization.products" id={value}>
              {record.product_display_name ??
                record.store_product_identifier ??
                value}
            </ShowLink>
          )}
        />
        <Table.Column
          dataIndex="tier"
          title={f('tier')}
          sorter
          sortOrder={getSortOrder(sorters, 'tier')}
          render={(value: string) => <Tag color="blue">{value}</Tag>}
        />
        <Table.Column
          dataIndex="subscription_id"
          title={f('subscription_id')}
          render={(value: number | null) =>
            value != null ? (
              <ShowLink
                resource="monetization.revenuecat_subscriptions"
                id={value}
              />
            ) : (
              '-'
            )
          }
        />
        <Table.Column
          dataIndex="entitlement_id"
          title={f('entitlement_id')}
          render={(value: number | null) =>
            value != null ? (
              <ShowLink resource="monetization.entitlements" id={value} />
            ) : (
              '-'
            )
          }
        />
        <Table.Column
          dataIndex="projection_failure_reason"
          title={f('projection_failure_reason')}
          render={(value: EntitlementProjectionFailure | null) => (
            <ProjectionFailureTag value={value} />
          )}
        />
        <Table.Column
          dataIndex="will_renew"
          title={f('will_renew')}
          sorter
          sortOrder={getSortOrder(sorters, 'will_renew')}
          render={(value: boolean) => (
            <Tag color={value ? 'success' : 'default'}>
              {value ? t('common.yes') : t('common.no')}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="valid_from"
          title={f('valid_from')}
          sorter
          sortOrder={getSortOrder(sorters, 'valid_from')}
          render={(value: string) =>
            value ? <DateField value={value} format="LLL" /> : '-'
          }
        />
        <Table.Column
          dataIndex="valid_until"
          title={f('valid_until')}
          sorter
          sortOrder={getSortOrder(sorters, 'valid_until')}
          render={(value: string) =>
            value ? <DateField value={value} format="LLL" /> : '-'
          }
        />
        <Table.Column
          dataIndex="store"
          title={f('store')}
          sorter
          sortOrder={getSortOrder(sorters, 'store')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />
        <Table.Column
          dataIndex="transaction_id"
          title={f('transaction_id')}
          render={(value: string) => (
            <span style={{ fontFamily: 'monospace' }}>{value}</span>
          )}
        />
      </Table>
    </Flex>
  );
};
